/**
 * Option and value labels, resolved from the ONE catalogue.
 * OWNER: web-frontend-engineer.
 *
 * No label is authored here. Two things live in this file and only two:
 *
 *  1. THE GROUPING AND ORDER of the option column — a layout decision taken by
 *     `FLOWS_SPEC` §6.3 ("grouped per garment in the order the workshop works")
 *     rather than by `rules.json`. It carries no rule and decides no outcome.
 *
 * That is now the ONLY thing in this file. The narrow alias table that pointed
 * `waistcoat.buttons_material` and `waistcoat.lining_colour` at the jacket's
 * value strings is deleted: the UX Writer gave the waistcoat its own fourteen
 * keys (COPY_DECK §11), so every value resolves from its own key and there is
 * no indirection left to drift.
 */

import { tDynamic, type CopyKey } from '@pt/copy';

export interface OptionGroupSpec {
  readonly garment: 'commission' | 'jacket' | 'trousers' | 'waistcoat';
  readonly headingKey: CopyKey;
  readonly optionIds: readonly string[];
}

/** Jacket → structure, body, lapel, pockets, sleeve, lining/finish, personalisation. */
export const OPTION_GROUPS: readonly OptionGroupSpec[] = [
  {
    garment: 'commission',
    headingKey: 'config.group.style',
    optionIds: ['formal.waist_covering'],
  },
  {
    garment: 'jacket',
    headingKey: 'config.group.structure',
    optionIds: ['jacket.canvas', 'jacket.shoulder'],
  },
  {
    garment: 'jacket',
    headingKey: 'config.group.body',
    optionIds: [
      'jacket.style',
      'jacket.button_stance',
      'jacket.vents',
      'jacket.quarters',
      'jacket.length',
    ],
  },
  {
    garment: 'jacket',
    headingKey: 'config.group.lapel',
    optionIds: [
      'jacket.lapel',
      'jacket.lapel_width',
      'jacket.gorge_height',
      'jacket.lapel_facing',
      'jacket.lapel_buttonhole',
    ],
  },
  {
    garment: 'jacket',
    headingKey: 'config.group.pockets',
    optionIds: [
      'jacket.hip_pockets',
      'jacket.ticket_pocket',
      'jacket.breast_pocket',
      'jacket.interior_pockets',
    ],
  },
  {
    garment: 'jacket',
    headingKey: 'config.group.sleeve',
    optionIds: [
      'jacket.sleeve_button_count',
      'jacket.functional_cuffs',
      'jacket.kissing_buttons',
    ],
  },
  {
    garment: 'jacket',
    headingKey: 'config.group.liningFinish',
    optionIds: [
      'jacket.lining_coverage',
      'jacket.lining_colour',
      'jacket.buttons_material',
      'jacket.pick_stitching',
      'jacket.buttonhole_thread',
      'jacket.edge_finish',
    ],
  },
  {
    garment: 'jacket',
    headingKey: 'config.group.personalisation',
    optionIds: [
      'jacket.monogram_placement',
      'jacket.monogram_text',
      'jacket.monogram_thread',
      'jacket.monogram_font',
    ],
  },
  {
    garment: 'trousers',
    headingKey: 'config.group.front',
    optionIds: ['trouser.front', 'trouser.pleat_direction'],
  },
  { garment: 'trousers', headingKey: 'config.group.rise', optionIds: ['trouser.rise'] },
  {
    garment: 'trousers',
    headingKey: 'config.group.waistband',
    optionIds: [
      'trouser.waistband_style',
      'trouser.belt_loops',
      'trouser.side_adjusters',
      'trouser.suspender_buttons',
    ],
  },
  {
    garment: 'trousers',
    headingKey: 'config.group.leg',
    optionIds: ['trouser.leg_taper', 'trouser.side_pockets', 'trouser.back_pockets'],
  },
  {
    garment: 'trousers',
    headingKey: 'config.group.hem',
    optionIds: ['trouser.cuffs', 'trouser.hem_opening_cm'],
  },
  {
    garment: 'trousers',
    headingKey: 'config.group.finish',
    optionIds: ['trouser.lining', 'trouser.fly', 'trouser.braid', 'trouser.crease'],
  },
  {
    garment: 'waistcoat',
    headingKey: 'config.group.style',
    optionIds: ['waistcoat.style', 'waistcoat.lapel'],
  },
  {
    garment: 'waistcoat',
    headingKey: 'config.group.front',
    optionIds: ['waistcoat.button_count'],
  },
  { garment: 'waistcoat', headingKey: 'config.group.back', optionIds: ['waistcoat.back'] },
  { garment: 'waistcoat', headingKey: 'config.group.bottom', optionIds: ['waistcoat.bottom'] },
  { garment: 'waistcoat', headingKey: 'config.group.pockets', optionIds: ['waistcoat.pockets'] },
  {
    garment: 'waistcoat',
    headingKey: 'config.group.finish',
    optionIds: ['waistcoat.buttons_material', 'waistcoat.lining_colour'],
  },
];

export const GARMENT_HEADING: Readonly<Record<string, CopyKey>> = {
  jacket: 'config.garment.jacket',
  trousers: 'config.garment.trousers',
  waistcoat: 'config.garment.waistcoat',
};

export function optionLabel(optionId: string): string {
  return tDynamic(`config.option.${optionId}.label`);
}

export function optionValueLabel(optionId: string, valueId: string | number | boolean): string {
  return tDynamic(`config.option.${optionId}.value.${String(valueId)}`);
}

export function commissionTypeLabel(value: string): string {
  return tDynamic(`config.option.commission.type.value.${value}`);
}

export function occasionLabel(value: string): string {
  return tDynamic(`config.option.commission.occasion.value.${value}`);
}
